import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { getSpiderWebWeights, updateSpiderWebWeights } from '../api/knowledgePacks';

/**
 * NEXUS V6.3 Phase 2C - Spider Web Interface (Executor)
 * 7-axis weight configuration (Product, Campaign, Promotion, Ambassador, Trend, Health, Values)
 * Health=100, Values=100 (locked)
 * Mobile-first 390×844, EMZI CI styling
 */

const SpiderWeb = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useLang();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [weights, setWeights] = useState({
    product: 50,
    campaign: 50,
    promotion: 50,
    ambassador: 50,
    trend: 50,
    health: 100, // Locked
    values: 100   // Locked
  });

  // Load user's weights on mount
  useEffect(() => {
    loadWeights();
  }, []);

  const loadWeights = async () => {
    if (!user?.user_id) return;
    
    try {
      setLoading(true);
      // Don't pass role as project_id - we want the default weights
      const response = await getSpiderWebWeights(user.user_id);
      
      console.log('[Spider Web] GET response:', response);
      
      // Response is the weights document: {user_id, weights: {...}, updated_at, ...}
      if (response && response.weights) {
        // Ensure all values are numbers 0-100
        const cleanWeights = {};
        for (const [key, value] of Object.entries(response.weights)) {
          cleanWeights[key] = typeof value === 'number' ? Math.min(100, Math.max(0, value)) : (key === 'health' || key === 'values' ? 100 : 50);
        }
        setWeights(cleanWeights);
        console.log('[Spider Web] Loaded weights:', cleanWeights);
      } else {
        console.log('[Spider Web] No weights in response, using defaults');
      }
    } catch (err) {
      console.error('[Spider Web] Failed to load weights:', err);
      // Use defaults on error
    } finally {
      setLoading(false);
    }
  };

  const handleWeightChange = (axis, value) => {
    setWeights(prev => ({
      ...prev,
      [axis]: parseInt(value, 10)
    }));
  };

  const handleSave = async () => {
    if (!user?.user_id) return;

    try {
      setSaving(true);
      
      // Optimistic update - update UI immediately
      const updatedWeights = { ...weights };
      
      // Save to backend (don't pass role as second parameter)
      await updateSpiderWebWeights(
        user.user_id,
        {
          user_id: user.user_id,
          weights: updatedWeights,
          is_default: true,
          updated_at: new Date().toISOString(),
        }
      );
      
      // Force refresh from backend to ensure consistency
      await loadWeights();
      
      alert(t('spider.savedSuccess'));
    } catch (err) {
      console.error('Failed to save weights:', err);
      alert(t('spider.savedError'));
      // Reload to revert optimistic update on error
      await loadWeights();
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setWeights({
      product: 50,
      campaign: 50,
      promotion: 50,
      ambassador: 50,
      trend: 50,
      health: 100,
      values: 100
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-3">⏳</div>
          <p className="text-sm font-semibold text-gray-700">{t('spider.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white px-4 py-3 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-lg">
              🕸️
            </div>
            <div>
              <h1 className="text-lg font-black leading-tight">{t('spider.title')}</h1>
              <p className="text-xs font-medium opacity-90">{t('spider.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold transition"
          >
            {t('spider.back')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 pb-24 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 68px)' }}>
        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
          <p className="text-xs font-semibold text-blue-900 mb-1">{t('spider.infoTitle')}</p>
          <p className="text-xs text-blue-800">
            {t('spider.infoDescription')}
          </p>
        </div>

        {/* Radar Chart Visualization (Simple) */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-4">
          <h3 className="text-sm font-bold text-gray-900 mb-3">{t('spider.currentWeights')}</h3>
          <div className="space-y-2">
            {Object.entries(weights).map(([axis, value]) => {
              const isLocked = axis === 'health' || axis === 'values';
              const color = 
                axis === 'product' ? 'bg-purple-500' :
                axis === 'campaign' ? 'bg-pink-500' :
                axis === 'promotion' ? 'bg-orange-500' :
                axis === 'ambassador' ? 'bg-yellow-500' :
                axis === 'trend' ? 'bg-green-500' :
                axis === 'health' ? 'bg-red-500' :
                'bg-blue-500';
              
              const label = 
                axis === 'product' ? `📦 ${t('spider.product')}` :
                axis === 'campaign' ? `🎯 ${t('spider.campaign')}` :
                axis === 'promotion' ? `💰 ${t('spider.promotion')}` :
                axis === 'ambassador' ? `👤 ${t('spider.ambassador')}` :
                axis === 'trend' ? `📈 ${t('spider.trend')}` :
                axis === 'health' ? `❤️ ${t('spider.health')}` :
                `⭐ ${t('spider.values')}`;

              return (
                <div key={axis}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-700">{label}</span>
                    <span className={`text-xs font-bold ${isLocked ? 'text-gray-400' : 'text-gray-900'}`}>
                      {value}%
                      {isLocked && <span className="ml-1 text-[10px]">🔒</span>}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${color} transition-all duration-300`}
                      style={{ width: `${value}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sliders */}
        <div className="space-y-3">
          {/* Product */}
          <SliderCard
            icon="📦"
            label={t('spider.product')}
            value={weights.product}
            onChange={(v) => handleWeightChange('product', v)}
            color="purple"
            sliderHint={t('spider.sliderHint')}
          />

          {/* Campaign */}
          <SliderCard
            icon="🎯"
            label={t('spider.campaign')}
            value={weights.campaign}
            onChange={(v) => handleWeightChange('campaign', v)}
            color="pink"
            sliderHint={t('spider.sliderHint')}
          />

          {/* Promotion */}
          <SliderCard
            icon="💰"
            label={t('spider.promotion')}
            value={weights.promotion}
            onChange={(v) => handleWeightChange('promotion', v)}
            color="orange"
            sliderHint={t('spider.sliderHint')}
          />

          {/* Ambassador */}
          <SliderCard
            icon="👤"
            label={t('spider.ambassador')}
            value={weights.ambassador}
            onChange={(v) => handleWeightChange('ambassador', v)}
            color="yellow"
            sliderHint={t('spider.sliderHint')}
          />

          {/* Trend */}
          <SliderCard
            icon="📈"
            label={t('spider.trend')}
            value={weights.trend}
            onChange={(v) => handleWeightChange('trend', v)}
            color="green"
            sliderHint={t('spider.sliderHint')}
          />

          {/* Health (Locked) */}
          <div className="bg-gray-100 rounded-xl shadow-sm p-4 border-2 border-gray-300">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl">❤️</span>
                <div>
                  <h4 className="text-sm font-bold text-gray-700">{t('spider.health')}</h4>
                  <p className="text-[10px] text-gray-500">{t('spider.locked')}</p>
                </div>
              </div>
              <span className="text-sm font-black text-gray-600">🔒 100%</span>
            </div>
            <div className="h-2 bg-gray-300 rounded-full overflow-hidden">
              <div className="h-full bg-red-500" style={{ width: '100%' }} />
            </div>
          </div>

          {/* Values (Locked) */}
          <div className="bg-gray-100 rounded-xl shadow-sm p-4 border-2 border-gray-300">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⭐</span>
                <div>
                  <h4 className="text-sm font-bold text-gray-700">{t('spider.values')}</h4>
                  <p className="text-[10px] text-gray-500">{t('spider.locked')}</p>
                </div>
              </div>
              <span className="text-sm font-black text-gray-600">🔒 100%</span>
            </div>
            <div className="h-2 bg-gray-300 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500" style={{ width: '100%' }} />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 space-y-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white text-base font-bold rounded-lg shadow-md hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {saving ? t('spider.saving') : t('spider.save')}
          </button>
          <button
            onClick={handleReset}
            disabled={saving}
            className="w-full py-2.5 bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {t('spider.reset')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Slider Card Component
const SliderCard = ({ icon, label, value, onChange, color, sliderHint }) => {
  const colorClasses = {
    purple: 'bg-purple-500',
    pink: 'bg-pink-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-500',
    green: 'bg-green-500',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{icon}</span>
          <div>
            <h4 className="text-sm font-bold text-gray-900">{label}</h4>
            <p className="text-[10px] text-gray-500">{sliderHint}</p>
          </div>
        </div>
        <span className="text-sm font-black text-gray-900">{value}%</span>
      </div>
      <input
        type="range"
        min="0"
        max="100"
        step="5"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-2 rounded-lg appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, ${colorClasses[color] === 'bg-purple-500' ? '#a855f7' : 
            colorClasses[color] === 'bg-pink-500' ? '#ec4899' : 
            colorClasses[color] === 'bg-orange-500' ? '#f97316' : 
            colorClasses[color] === 'bg-yellow-500' ? '#eab308' : '#22c55e'} 0%, ${
            colorClasses[color] === 'bg-purple-500' ? '#a855f7' : 
            colorClasses[color] === 'bg-pink-500' ? '#ec4899' : 
            colorClasses[color] === 'bg-orange-500' ? '#f97316' : 
            colorClasses[color] === 'bg-yellow-500' ? '#eab308' : '#22c55e'} ${value}%, #e5e7eb ${value}%, #e5e7eb 100%)`
        }}
      />
      <div className="flex justify-between mt-1">
        <span className="text-[10px] font-medium text-gray-500">0%</span>
        <span className="text-[10px] font-medium text-gray-500">50%</span>
        <span className="text-[10px] font-medium text-gray-500">100%</span>
      </div>
    </div>
  );
};

export default SpiderWeb;
